import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { formatDateTimeObject } from '../../utils/helpers';

const TravelPost = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef(null);
  const [dateRange, setDateRange] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [source, setSource] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState({ 
    id: '', 
    description: '', 
    travelDate: '', 
    duration: '', 
    budget: '', 
    adult: 1, 
    child: 0, 
    haveTicket: false, 
    hotelCategory: [], 
    willBook: '', 
    preferredCallTime: '', 
    tourType: '',
    leavingFrom: '',
    goingTo: '',
    contactedAgent: '',
    datesFlexible: '',
    mostImportant: ''
  });
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [dmcModal, setDmcModal] = useState(false);
  const [dmcData, setDmcData] = useState(null);
  const [dmcLoadingId, setDmcLoadingId] = useState(null);
  const [leavingFromKeyword, setLeavingFromKeyword] = useState('');
  const [goingToKeyword, setGoingToKeyword] = useState('');
  const [leavingFromOptions, setLeavingFromOptions] = useState([]);
  const [goingToOptions, setGoingToOptions] = useState([]);
  const [leavingFromLoading, setLeavingFromLoading] = useState(false);
  const [goingToLoading, setGoingToLoading] = useState(false);
  const [leavingFromActive, setLeavingFromActive] = useState(false);
  const [goingToActive, setGoingToActive] = useState(false);
  const dateInputRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        date: dateRange,
        callStatus: callStatus,
        source: source,
        budgetMin: budgetMin,
        budgetMax: budgetMax
      };
      
      const response = await apiService.post('travelPostList', params);
      if (response && response.data) {
        console.log("response:: ", response.data);
        setPosts(response.data);
        setTotalRecords(response.recordsFiltered || 0);
      }
    } catch (error) {
      toast.error('Failed to load travel posts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, dateRange, callStatus, source, budgetMin, budgetMax]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        setLeavingFromActive(false);
        setGoingToActive(false);
        setLeavingFromOptions([]);
        setGoingToOptions([]);
      }
    };
    
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, []);

  useEffect(() => {
    if (!leavingFromActive) {
      setLeavingFromOptions([]);
      return;
    }

    const keyword = leavingFromKeyword.trim();
    if (!keyword) {
      setLeavingFromOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setLeavingFromLoading(true);
        const response = await apiService.post('locationSearch', { keyword });
        setLeavingFromOptions(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        setLeavingFromOptions([]);
      } finally {
        setLeavingFromLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [leavingFromActive, leavingFromKeyword]);

  useEffect(() => {
    if (!goingToActive) {
      setGoingToOptions([]);
      return;
    }

    const keyword = goingToKeyword.trim();
    if (!keyword) {
      setGoingToOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setGoingToLoading(true);
        const response = await apiService.post('locationSearch', { keyword });
        setGoingToOptions(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        setGoingToOptions([]);
      } finally {
        setGoingToLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [goingToActive, goingToKeyword]);

  useEffect(() => {
    if (dateInputRef.current && window.$) {
      window.$(dateInputRef.current).daterangepicker({
        autoUpdateInput: false,
        locale: { format: 'DD/MM/YYYY', cancelLabel: 'Clear' }
      });
      window.$(dateInputRef.current).on('apply.daterangepicker', (ev, picker) => {
        const range = picker.startDate.format('DD/MM/YYYY') + ' - ' + picker.endDate.format('DD/MM/YYYY');
        setDateRange(range);
        setCurrentPage(1);
      });
      window.$(dateInputRef.current).on('cancel.daterangepicker', () => {
        setDateRange('');
        setCurrentPage(1);
      });
    }
  }, []);

  const normalizeLocationInput = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    if (Array.isArray(location) && location[0]) return location[0]._id || location[0].id || '';
    if (location._id) return location._id;
    if (location.id) return location.id;
    return '';
  };

  const handleEdit = (post) => {
    setEditData({ 
      id: post._id, 
      description: post.description || '', 
      travelDate: post.travelDate ? new Date(post.travelDate).toISOString().split('T')[0] : '', 
      duration: post.duration || '', 
      budget: post.budget ?? '', 
      adult: post.adult || 1,
      child: post.child || 0, 
      haveTicket: post.haveTicket || false, 
      hotelCategory: post.hotelCategory || [], 
      willBook: post.willBook || '', 
      preferredCallTime: post.preferredCallTime || '', 
      tourType: post.tourType || '',
      leavingFrom: normalizeLocationInput(post.leavingFrom || post.leavingFromData),
      goingTo: normalizeLocationInput(post.goingTo || post.goingToData),
      contactedAgent: post.contactedAgent || '',
      datesFlexible: post.datesFlexible || '',
      mostImportant: post.mostImportant || ''
    });
    const leavingFromLabel = post.leavingFrom ? getLocationName(post.leavingFrom) : (post.leavingFromData ? getLocationName(post.leavingFromData) : '--');
    const goingToLabel = post.goingTo ? getLocationName(post.goingTo) : (post.goingToData ? getLocationName(post.goingToData) : '--');
    setLeavingFromKeyword(leavingFromLabel !== '--' ? leavingFromLabel : '');
    setGoingToKeyword(goingToLabel !== '--' ? goingToLabel : '');
    setLeavingFromOptions([]);
    setGoingToOptions([]);
    setLeavingFromActive(false);
    setGoingToActive(false);
    setEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await apiService.post('editTravelPost', editData);
      if (!response.success) {
        toast.success('Travel post updated successfully');
        setEditModal(false);
        fetchPosts();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const response = await apiService.get(`deleteTravelPost/${deleteId}`);
      if (!response.success) {
        toast.success('Travel Post deleted successfully');
        setDeleteModal(false);
        fetchPosts();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDmc = async (post) => {
    try {
      setDmcLoadingId(post._id);
      
      // Get the location ID - handle both object and string formats
      let locationId = '';
      if (post?.goingTo) {
        if (typeof post.goingTo === 'string') {
          locationId = post.goingTo;
        } else if (post.goingTo._id) {
          locationId = post.goingTo._id;
        } else if (Array.isArray(post.goingTo) && post.goingTo[0]?._id) {
          locationId = post.goingTo[0]._id;
        }
      }
      
      if (!locationId) {
        toast.error('Location information is missing');
        setDmcLoadingId(null);
        return;
      }
      
      // Build query params for matching
      const params = new URLSearchParams({
        location: locationId,
        duration: post?.duration || ''
      });

      const response = await apiService.get(`dmcItineraries?${params.toString()}`);
      if (response?.status === true && response?.data) {
        setDmcData(response.data);
        setDmcModal(true);
      } else {
        toast.error('No matching DMC data found');
        setDmcData(null);
      }
    } catch (error) {
      console.error('Error fetching DMC data:', error);
      toast.error('Failed to fetch DMC data');
      setDmcData(null);
    } finally {
      setDmcLoadingId(null);
    }
  };

  const updateCallStatus = async (id, status) => {
    setPosts(prevPosts => prevPosts.map(post => 
      post._id === id ? { ...post, callStatus: status } : post
    ));
    try {
      const response = await apiService.post(`updateCallStatus/${id}`, { callStatus: status });
      if (response.status) {
        toast.success('Call status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update call status');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Something went wrong, try again!');
      fetchPosts();
    }
  };

  const updateCloseStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    setPosts(prevPosts => prevPosts.map(post => 
      post._id === id ? { ...post, isClose: newStatus } : post
    ));
    try {
      const response = await apiService.post(`updateCloseStatus/${id}`, { closeStatus: newStatus });
      if (response.status) {
        toast.success('Close status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update close status');
        setPosts(prevPosts => prevPosts.map(post => 
          post._id === id ? { ...post, isClose: currentStatus } : post
        ));
      }
    } catch (error) {
      toast.error('Something went wrong, try again!');
      setPosts(prevPosts => prevPosts.map(post => 
        post._id === id ? { ...post, isClose: currentStatus } : post
      ));
    }
  };

  const selectLeavingFrom = (location) => {
    setEditData((prev) => ({ ...prev, leavingFrom: location._id }));
    setLeavingFromKeyword(getLocationName(location));
    setLeavingFromOptions([]);
    setLeavingFromActive(false);
  };

  const selectGoingTo = (location) => {
    setEditData((prev) => ({ ...prev, goingTo: location._id }));
    setGoingToKeyword(getLocationName(location));
    setGoingToOptions([]);
    setGoingToActive(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const formatBool = (value) => (value ? 'Yes' : 'No');

  const getAddedToCrmLabel = (post) => {
    const addedBy = post?.addedToTravellinglarkByDetails;
    const assignedTo = post?.addedToTravellinglarkAssignedToDetails;
    
    if (!addedBy) return '--';

    // Get the name of who added it (admin/staff) - handle all possible field combinations
    let addedByName = '';
    
    // Try business_name first (for vendors/staff with business)
    if (addedBy.business_name && addedBy.business_name.trim()) {
      addedByName = addedBy.business_name;
    } else {
      // Fall back to first_name + last_name
      const firstName = addedBy.first_name?.trim() || '';
      const lastName = addedBy.last_name?.trim() || '';
      if (firstName || lastName) {
        addedByName = `${firstName} ${lastName}`.trim();
      } else if (addedBy.email) {
        addedByName = addedBy.email;
      }
    }

    // Build label with both admin/staff name and assigned name
    let label = addedByName;
    if (assignedTo?.name && assignedTo.name.trim()) {
      label = `${addedByName} Assigned to: ${assignedTo.name}`;
    }

    return label || '--';
  };

  const getLocationName = (location) => {
    if (!location) return '--';

    const formatLocationObject = (loc) => {
      if (!loc || typeof loc !== 'object') return '--';
      const state = loc?.state?.[0]?.name || loc?.stateId?.name;
      const country = loc?.country?.[0]?.name || loc?.countryId?.name;
      const name = loc?.name;
      const formatted = [name, state, country].filter(Boolean).join(', ');
      return formatted || '--';
    };

    if (Array.isArray(location) && location[0]) {
      return formatLocationObject(location[0]);
    }
    
    if (typeof location === 'object') {
      return formatLocationObject(location);
    }
    
    return '--';
  };

  const [copied, setCopied] = useState(false);
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [sharedRowId, setSharedRowId] = useState(null);
  const [sharedModal, setSharedModal] = useState(false);
  const [crmMembers, setCrmMembers] = useState([]);
  const [crmMembersLoading, setCrmMembersLoading] = useState(false);
  const [selectedCrmMember, setSelectedCrmMember] = useState('');

  const getShareUrl = (post) => `https://www.travellinglark.com/travel-lead/details/${post.id}`;

  const handleShareRow = (e, post) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getShareUrl(post)).then(() => {
      setSharedRowId(post._id);
      setTimeout(() => setSharedRowId(null), 2000);
    });
  };

  const handleShareModal = () => {
    navigator.clipboard.writeText(getShareUrl(selectedPost)).then(() => {
      setSharedModal(true);
      setTimeout(() => setSharedModal(false), 2000);
    });
  };
  const [sendingLeadId, setSendingLeadId] = useState(null);
  const [sendLeadModal, setSendLeadModal] = useState(false);
  const [sendLeadPost, setSendLeadPost] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCrmMembers = async () => {
      try {
        setCrmMembersLoading(true);
        const response = await apiService.get('leads/members');
        const members = Array.isArray(response?.data) ? response.data : [];

        if (!isMounted) return;

        setCrmMembers(members);
        const ownerOption = members.find((member) => member.type === 'owner');
        setSelectedCrmMember(ownerOption?._id || members[0]?._id || '');
      } catch (error) {
        if (!isMounted) return;
        setCrmMembers([]);
        setSelectedCrmMember('');
        toast.error('Failed to load CRM members');
      } finally {
        if (isMounted) {
          setCrmMembersLoading(false);
        }
      }
    };

    fetchCrmMembers();

    return () => {
      isMounted = false;
    };
  }, []);

  const closeSendLeadModal = () => {
    const ownerOption = crmMembers.find((member) => member.type === 'owner');
    setSendLeadModal(false);
    setSendLeadPost(null);
    setSelectedCrmMember(ownerOption?._id || crmMembers[0]?._id || '');
  };

  const sendToMyLeads = async () => {
    if (!sendLeadPost) return;
    setSendingLeadId(sendLeadPost._id);
    try {
      // Calculate total budget as: budget per person * number of adults
      const totalBudget = sendLeadPost.budget && sendLeadPost.adult 
        ? sendLeadPost.budget * sendLeadPost.adult 
        : sendLeadPost.budget;
      
      const response = await apiService.post('leads/send-to-my-leads', {
        queryId: sendLeadPost._id,
        assignedTo: selectedCrmMember || null,
        budget: totalBudget
      });
      if (response.status) {
        toast.success('Lead added to My Leads successfully');
      } else {
        toast.error(response.message || 'Failed to add lead');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSendingLeadId(null);
      closeSendLeadModal();
    }
  };

  const copyLeadData = (post) => {
    const text = [
      `From: ${getLocationName(post.leavingFrom)}`,
      `To: ${getLocationName(post.goingTo)}`,
      `Travel Date: ${formatDate(post.travelDate)}`,
      `Adults / Children: ${post.adult ?? '--'} / ${post.child ?? '--'}`,
      `Hotel Category: ${post.hotelCategory?.length ? post.hotelCategory.join(', ') : '--'}`,
      `Duration: ${post.duration || '--'}`,
      `Tour Type: ${post.tourType || '--'}`,
      `Will Book: ${post.willBook || '--'}`,
      `Ticket: ${formatBool(post.haveTicket)}`,
      `Dates Flexible: ${post.datesFlexible || '--'}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyLeadDataRow = (e, post) => {
    e.stopPropagation();
    const text = [
      `From: ${getLocationName(post.leavingFrom)}`,
      `To: ${getLocationName(post.goingTo)}`,
      `Travel Date: ${formatDate(post.travelDate)}`,
      `Adults / Children: ${post.adult ?? '--'} / ${post.child ?? '--'}`,
      `Hotel Category: ${post.hotelCategory?.length ? post.hotelCategory.join(', ') : '--'}`,
      `Duration: ${post.duration || '--'}`,
      `Tour Type: ${post.tourType || '--'}`,
      `Will Book: ${post.willBook || '--'}`,
      `Ticket: ${formatBool(post.haveTicket)}`,
      `Dates Flexible: ${post.datesFlexible || '--'}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedRowId(post._id);
      setTimeout(() => setCopiedRowId(null), 2000);
    });
  };

  const openViewModal = (post) => {
    setSelectedPost(post);
    setViewModal(true);
  };

  const formatPagePath = (page) => {
    if (!page) return '--';
    return page.replace(/^https?:\/\/(www\.)?travellinglark\.com/i, '') || '/';
  };

  const getAiResultBadge = (airesult) => {
    const intent = airesult?.intent || 'LOW';
    const config = {
      HIGH: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      MEDIUM: 'bg-amber-50 text-amber-700 border border-amber-100',
      LOW: 'bg-slate-100 text-slate-700 border border-slate-200'
    };

    return {
      label: intent,
      color: config[intent] || config.LOW
    };
  };

  const formatAiScore = (airesult) => {
    if (airesult?.finalScore !== undefined && airesult?.finalScore !== null) return airesult.finalScore;
    if (airesult?.score !== undefined && airesult?.score !== null) return airesult.score;
    return '--';
  };

  const getCallStatusBadge = (status) => {
    const config = {
      pending: 'bg-amber-50 text-amber-700 border border-amber-100',
      connected: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      not_connected: 'bg-rose-50 text-rose-700 border border-rose-100',
      busy: 'bg-sky-50 text-sky-700 border border-sky-100',
      no_answer: 'bg-slate-100 text-slate-700 border border-slate-200',
      call_back: 'bg-violet-50 text-violet-700 border border-violet-100'
    };
    const labels = {
      pending: 'Pending',
      connected: 'Connected',
      not_connected: 'Not Connected',
      busy: 'Busy',
      no_answer: 'No Answer',
      call_back: 'Call Back'
    };
    return { color: config[status] || config.pending, label: labels[status] || 'Pending' };
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Travel Posts</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => {
                    setKeyword(val);
                    setCurrentPage(1);
                  }, 500);
                }}
                placeholder="Type ID, Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <input
              ref={dateInputRef}
              type="text"
              value={dateRange}
              readOnly
              placeholder="DD/MM/YYYY - DD/MM/YYYY"
              className="w-full sm:w-[220px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer"
            />
            <select
              value={callStatus}
              onChange={(e) => { setCallStatus(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[180px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Call Status</option>
              <option value="pending">Pending</option>
              <option value="connected">Connected</option>
              <option value="not_connected">Not Connected</option>
              <option value="busy">Busy</option>
              <option value="no_answer">No Answer</option>
              <option value="call_back">Call Back</option>
            </select>
            <select
              value={source}
              onChange={(e) => { setSource(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[180px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Sources</option>
              <option value="meta">Meta</option>
              <option value="google">Google</option>
              <option value="other">Other</option>
            </select>
            <div className="flex items-center gap-1 w-full sm:w-auto">
              <input
                type="number"
                min="0"
                value={budgetMin}
                onChange={(e) => { setBudgetMin(e.target.value); setCurrentPage(1); }}
                placeholder="Min Budget"
                className="w-full sm:w-[110px] px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-gray-400 text-sm">-</span>
              <input
                type="number"
                min="0"
                value={budgetMax}
                onChange={(e) => { setBudgetMax(e.target.value); setCurrentPage(1); }}
                placeholder="Max Budget"
                className="w-full sm:w-[110px] px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={fetchPosts}
              disabled={loading}
              className="inline-flex items-center justify-center h-[38px] w-[38px] border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 flex-shrink-0"
              title="Refresh"
            >
              <i className={`bi bi-arrow-clockwise text-base ${loading ? 'animate-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-signpost-2 text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Travel Posts Found</h3>
              <p className="text-gray-500">No posts match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-4 py-3">#</th>
                    <th className="px-2 sm:px-4 py-3">User Details</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[280px]">Query Details</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[180px]">Trip Info</th>
                    {user?.role !== 'staff' && (
                      <th className="px-2 sm:px-4 py-3 w-[240px] min-w-[240px]">Ad ID / Page</th>
                    )}
                    <th className="px-2 sm:px-4 py-3">Buyers</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[220px]">Status Details</th>
                    <th className="px-2 sm:px-4 py-3 text-center min-w-[108px]">Action</th>
                    {user?.role === 'admin' && (
                      <th className="px-2 sm:px-4 py-3 min-w-[140px]">Added to CRM</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {posts.map((post, index) => {
                    const created = formatDateTimeObject(post.createdAt);
                    const statusBadge = getCallStatusBadge(post.callStatus);
                    const aiBadge = getAiResultBadge(post.airesult);
                    return (
                      <tr key={post._id} onClick={() => openViewModal(post)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-2 sm:px-4 py-3">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-xs text-blue-600 font-semibold mb-1">ID: {post.id}</span>
                            <span className="font-semibold text-gray-800">{post.userId?.first_name} {post.userId?.last_name}</span>
                            <span className="text-xs text-gray-500"><i className="bi bi-envelope mr-1"></i>{post.userId?.email || '-'}</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-500"><i className="bi bi-phone mr-1"></i>{post.userId?.phone || '-'}</span>
                              {post.userId?.phone ? (
                                <a
                                  href={`tel:${post.userId.phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors flex-shrink-0"
                                  title={`Call ${post.userId.phone}`}
                                >
                                  <i className="bi bi-telephone-fill" style={{fontSize:'9px'}}></i>
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 min-w-[280px]">
                          <div className="min-w-[240px] max-w-[340px] text-xs text-slate-700">
                            <div className="rounded-xl border border-slate-200 bg-white px-2.5 py-2">
                              <div className="flex items-start gap-2 text-[11px]">
                                <div className="flex flex-col items-center pt-0.5">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                  <span className="h-4 w-px bg-slate-200"></span>
                                  <span className="h-2 w-2 rounded-full bg-rose-500"></span>
                                </div>
                                <div className="min-w-0 flex-1 space-y-1.5">
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">From</span>
                                    <span className="break-words font-medium text-slate-700">{getLocationName(post.leavingFrom)}</span>
                                  </div>
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">To</span>
                                    <span className="break-words font-medium text-slate-700">{getLocationName(post.goingTo)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 text-[12px] text-slate-700 line-clamp-2" title={post.description}>
                              {post.description || <span className="text-gray-400">No description</span>}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-[0.04em] text-[10px] ${aiBadge.color}`}>
                                  {aiBadge.label}
                                </span>
                                <span className="text-[11px] text-slate-500">Score: {formatAiScore(post.airesult)}</span>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleViewDmc(post); }}
                                disabled={dmcLoadingId === post._id}
                                className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-50"
                                title="View DMC & Itinerary"
                              >
                                {dmcLoadingId === post._id
                                  ? <span className="animate-spin rounded-full h-2.5 w-2.5 border border-orange-700 border-t-transparent"></span>
                                  : <i className="bi bi-building text-xs"></i>
                                }
                              </button>
                            </div>
                            {post.closeReason && (
                              <div className="mt-2 text-[11px] text-rose-700 line-clamp-2" title={post.closeReason}>
                                Close Reason: {post.closeReason}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 min-w-[180px]">
                          <div className="flex flex-col gap-1 text-[11px] text-slate-700">
                            <div><span className="font-semibold text-slate-500">Date:</span> {formatDate(post.travelDate)}</div>
                            <div><span className="font-semibold text-slate-500">Pax:</span> {post.adult ?? '--'}A / {post.child ?? 0}C</div>
                            <div><span className="font-semibold text-slate-500">Duration:</span> {post.duration || '--'}</div>
                            <div><span className="font-semibold text-slate-500">Budget:</span> {(post.budget != null && post.adult != null) ? `₹${post.budget * post.adult} (${post.adult}A × ₹${post.budget})` : '--'}</div>
                            <div className="flex items-center justify-between gap-1">
                              <span><span className="font-semibold text-slate-500">Hotel:</span> {post.hotelCategory?.length ? post.hotelCategory.join(', ') : '--'}</span>
                              <button
                                onClick={(e) => copyLeadDataRow(e, post)}
                                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-all flex-shrink-0 ${
                                  copiedRowId === post._id
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                                }`}
                                title="Copy lead data"
                              >
                                <i className={`bi ${copiedRowId === post._id ? 'bi-check2' : 'bi-clipboard'}`}></i>
                                {copiedRowId === post._id ? 'Copied!' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        </td>
                        {user?.role !== 'staff' && (
                          <td className="px-2 sm:px-4 py-3 w-[240px] min-w-[240px]">
                            <div className="flex flex-col gap-1 text-xs sm:text-sm">
                              <span className="font-semibold text-slate-800">{post.source && post.adId ? `${post.source}-${post.adId}` : (post.source || post.adId || '-')}</span>
                              <span className="block max-w-[220px] truncate whitespace-nowrap text-slate-700" title={formatPagePath(post.page)}>
                                {formatPagePath(post.page)}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="px-2 sm:px-4 py-3 text-center">
                          <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => user?.role !== 'staff' ? setOpenDropdown(openDropdown === `buyers-${post._id}` ? null : `buyers-${post._id}`) : null}
                              className="px-2 sm:px-3 py-1 bg-[#009ef7]/10 text-[#009ef7] rounded text-xs font-semibold hover:bg-[#009ef7]/20"
                              disabled={user?.role === 'staff'}
                            >
                              {post.buyBy?.length || 0} Vendor{post.buyBy?.length > 1 ? 's' : ''}
                            </button>
                            {openDropdown === `buyers-${post._id}` && user?.role !== 'staff' && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48">
                                {post.buyBy?.length > 0 ? post.buyBy.map((vendor, i) => (
                                  <div key={i} className="px-2 sm:px-4 py-2 text-sm border-b last:border-b-0">
                                    <div className="font-semibold text-gray-900">{vendor.business_name || '-'}</div>
                                    <div className="text-gray-600">{vendor.first_name} {vendor.last_name}</div>
                                  </div>
                                )) : <div className="px-2 sm:px-4 py-2 text-sm text-gray-500">No vendors</div>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 min-w-[220px]" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-col gap-2 text-xs">
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-800">{created.date}</span>
                              <span className="text-xs text-gray-500">{created.time}</span>
                            </div>

                            <select
                              value={post.callStatus || 'pending'}
                              onChange={(e) => updateCallStatus(post._id, e.target.value)}
                              className={`text-xs px-2 py-1 rounded border-0 w-full ${statusBadge.color}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="connected">Connected</option>
                              <option value="not_connected">Not Connected</option>
                              <option value="busy">Busy</option>
                              <option value="no_answer">No Answer</option>
                              <option value="call_back">Call Back</option>
                            </select>
                            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2 border border-slate-200">
                              <span className="text-[11px] font-semibold text-slate-600">Close</span>
                              <button
                                onClick={() => updateCloseStatus(post._id, post.isClose)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${post.isClose ? 'bg-green-500' : 'bg-gray-300'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${post.isClose ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-col items-center gap-2">
                            <span className={`inline-block w-fit px-2 sm:px-3 py-1 rounded text-xs ${post.deletedAt ? 'bg-red-100 text-red-700' : 'bg-[#009ef7]/10 text-[#009ef7]'}`}>
                              {post.deletedAt ? 'Deleted' : 'Available'}
                            </span>
                            <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => openViewModal(post)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
                              title="View"
                            >
                              <i className="bi bi-eye text-sm"></i>
                            </button>
                            <button
                              onClick={(e) => handleShareRow(e, post)}
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                                sharedRowId === post._id
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                              }`}
                              title="Copy share link"
                            >
                              <i className={`bi ${sharedRowId === post._id ? 'bi-check2' : 'bi-share'} text-sm`}></i>
                            </button>
                            {(user?.role === 'staff' || user?.role === 'admin') && (
                              <button
                                onClick={() => { setSendLeadPost(post); setSendLeadModal(true); }}
                                disabled={sendingLeadId === post._id}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50"
                                title="Send to CRM"
                              >
                                {sendingLeadId === post._id
                                  ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-700"></span>
                                  : <i className="bi bi-person-plus text-sm"></i>
                                }
                              </button>
                            )}
                            {user?.role === 'admin' && (
                              <>
                                <button
                                  onClick={() => handleEdit(post)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-100 text-green-700 hover:bg-green-200"
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil text-sm"></i>
                                </button>
                                <button
                                  onClick={() => { setDeleteId(post._id); setDeleteModal(true); }}
                                  className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${post.deletedAt ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                  title={post.deletedAt ? 'Restore' : 'Delete'}
                                >
                                  <i className={`bi ${post.deletedAt ? 'bi-arrow-counterclockwise' : 'bi-trash'} text-sm`}></i>
                                </button>
                              </>
                            )}
                            </div>
                          </div>
                        </td>
                        {user?.role === 'admin' && (
                          <td className="px-2 sm:px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {post.isAddedToTravellinglark ? (
                              <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-semibold w-fit">
                                  <i className="bi bi-check-circle-fill text-[9px]"></i> Added
                                </span>
                                <span className="text-[10px] font-medium text-slate-700">
                                  {getAddedToCrmLabel(post)}
                                </span>
                                {post.addedToTravellinglarkAt && (
                                  <span className="text-[10px] text-slate-500">
                                    {formatDate(post.addedToTravellinglarkAt)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-semibold w-fit">
                                <i className="bi bi-dash-circle text-[9px]"></i> Not Added
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && posts.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-xs sm:text-sm text-gray-700">Show</span>
              <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">«</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>{page}</button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">»</button>
            </div>
          </div>
        )}
      </div>

      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] shadow-xl border border-slate-200 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0 bg-white">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Edit Travel Post</h3>
                <p className="text-[11px] text-slate-500">Update travel post details</p>
              </div>
              <button type="button" onClick={() => setEditModal(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="flex-1 min-h-0 flex flex-col">
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Leaving From</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search leaving from location"
                        value={leavingFromKeyword}
                        onChange={(e) => setLeavingFromKeyword(e.target.value)}
                        onFocus={() => setLeavingFromActive(true)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#009ef7]/30"
                      />
                      {leavingFromLoading && (
                        <div className="mt-2 text-[11px] text-slate-500">Searching locations...</div>
                      )}
                      {!leavingFromLoading && leavingFromKeyword.trim() && leavingFromOptions.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm z-50 absolute w-full">
                          {leavingFromOptions.map((location) => (
                            <button
                              key={location._id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectLeavingFrom(location);
                              }}
                              className="w-full px-3 py-2.5 text-left border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                            >
                              <div className="text-sm font-medium text-slate-800">{location.name}</div>
                              <div className="text-[11px] text-slate-500">{[location.stateId?.name, location.countryId?.name].filter(Boolean).join(', ') || '--'}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {!leavingFromLoading && leavingFromKeyword.trim() && leavingFromOptions.length === 0 && (
                        <div className="mt-2 text-[11px] text-slate-500">No locations found.</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Going To</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search going to location"
                        value={goingToKeyword}
                        onChange={(e) => setGoingToKeyword(e.target.value)}
                        onFocus={() => setGoingToActive(true)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#009ef7]/30"
                      />
                      {goingToLoading && (
                        <div className="mt-2 text-[11px] text-slate-500">Searching locations...</div>
                      )}
                      {!goingToLoading && goingToKeyword.trim() && goingToOptions.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm z-50 absolute w-full">
                          {goingToOptions.map((location) => (
                            <button
                              key={location._id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectGoingTo(location);
                              }}
                              className="w-full px-3 py-2.5 text-left border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                            >
                              <div className="text-sm font-medium text-slate-800">{location.name}</div>
                              <div className="text-[11px] text-slate-500">{[location.stateId?.name, location.countryId?.name].filter(Boolean).join(', ') || '--'}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {!goingToLoading && goingToKeyword.trim() && goingToOptions.length === 0 && (
                        <div className="mt-2 text-[11px] text-slate-500">No locations found.</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Travel Date</label>
                    <input
                      type="date"
                      value={editData.travelDate}
                      onChange={(e) => setEditData((prev) => ({ ...prev, travelDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Duration</label>
                    <input
                      type="text"
                      placeholder="e.g., 5 Days 4 Nights"
                      value={editData.duration}
                      onChange={(e) => setEditData((prev) => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Budget</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Enter budget"
                      value={editData.budget}
                      onChange={(e) => setEditData((prev) => ({ ...prev, budget: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Adults</label>
                    <input
                      type="number"
                      min="1"
                      value={editData.adult}
                      onChange={(e) => setEditData((prev) => ({ ...prev, adult: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Children</label>
                    <input
                      type="number"
                      min="0"
                      value={editData.child}
                      onChange={(e) => setEditData((prev) => ({ ...prev, child: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.haveTicket}
                        onChange={(e) => setEditData((prev) => ({ ...prev, haveTicket: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-slate-600">Have Ticket</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Description</label>
                  <textarea
                    rows="3"
                    placeholder="Enter description"
                    value={editData.description}
                    onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  />
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <p className="text-[11px] font-semibold text-slate-600 mb-2">Travel Preferences</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Tour Type</label>
                      <select
                        value={editData.tourType}
                        onChange={(e) => setEditData((prev) => ({ ...prev, tourType: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                      >
                        <option value="">Select tour type</option>
                        <option value="Honeymoon">Honeymoon</option>
                        <option value="Family">Family</option>
                        <option value="Adventure">Adventure</option>
                        <option value="Friends">Friends</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Will Book</label>
                      <select
                        value={editData.willBook}
                        onChange={(e) => setEditData((prev) => ({ ...prev, willBook: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                      >
                        <option value="">Select when to book</option>
                        <option value="In next 2-3 days">In next 2-3 days</option>
                        <option value="In this week">In this week</option>
                        <option value="In this month">In this month</option>
                        <option value="Next month">Next month</option>
                        <option value="May be later">May be later</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Preferred Call Time</label>
                    <select
                      value={editData.preferredCallTime}
                      onChange={(e) => setEditData((prev) => ({ ...prev, preferredCallTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="">Select preferred call time</option>
                      <option value="Anytime">Anytime</option>
                      <option value="Morning">Morning</option>
                      <option value="Afternoon">Afternoon</option>
                      <option value="Evening">Evening</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Hotel Category</label>
                    <div className="space-y-2">
                      {["5 Star", "4 Star", "3 Star", "2 Star"].map((category) => (
                        <label key={category} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editData.hotelCategory.includes(category)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditData((prev) => ({
                                ...prev,
                                hotelCategory: checked
                                  ? [...prev.hotelCategory, category]
                                  : prev.hotelCategory.filter((c) => c !== category)
                              }));
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-slate-600">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <p className="text-[11px] font-semibold text-slate-600 mb-2">Additional Information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Contacted Agent</label>
                      <select
                        value={editData.contactedAgent}
                        onChange={(e) => setEditData((prev) => ({ ...prev, contactedAgent: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Dates Flexible</label>
                      <select
                        value={editData.datesFlexible}
                        onChange={(e) => setEditData((prev) => ({ ...prev, datesFlexible: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Most Important</label>
                    <select
                      value={editData.mostImportant}
                      onChange={(e) => setEditData((prev) => ({ ...prev, mostImportant: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="">Select what's most important</option>
                      <option value="Best price">Best price</option>
                      <option value="Best hotels">Best hotels</option>
                      <option value="Smooth experience">Smooth experience</option>
                      <option value="Custom itinerary">Custom itinerary</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 shrink-0 bg-white border-t border-slate-100 px-4 py-3">
                <button type="button" onClick={() => setEditModal(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="px-3 py-2 text-sm bg-[#009ef7] text-white rounded-lg disabled:opacity-60">
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Confirmation</h3>
            <p className="mb-6 text-sm sm:text-base">Are you sure you want to delete this Travel Post?</p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} disabled={submitting} className="px-2 sm:px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-center">Cancel</button>
              <button onClick={handleDelete} disabled={submitting} className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModal && selectedPost && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] p-2 sm:p-5">
          <div className="mx-auto max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col font-sans">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-2.5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5">Lead Overview</h3>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-100 px-2.5 py-1 text-xs font-semibold">
                      <i className="bi bi-hash mr-1"></i>{selectedPost.id || '--'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-semibold">
                      Source: {selectedPost.source || '--'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-orange-50 text-orange-700 border border-orange-100 px-2.5 py-1 text-xs font-semibold">
                      Ad ID: {selectedPost.adId || '--'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 text-xs font-semibold">
                      Buyers: {selectedPost.buyBy?.length || 0}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${getCallStatusBadge(selectedPost.callStatus).color}`}>
                      {getCallStatusBadge(selectedPost.callStatus).label}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border ${selectedPost.deletedAt ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                      {selectedPost.deletedAt ? 'Deleted' : 'Available'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => copyLeadData(selectedPost)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      copied
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                    }`}
                    title="Copy lead data"
                  >
                    <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} text-sm`}></i>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleShareModal}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      sharedModal
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100'
                    }`}
                    title="Copy share link"
                  >
                    <i className={`bi ${sharedModal ? 'bi-check2' : 'bi-share'} text-sm`}></i>
                    {sharedModal ? 'Copied!' : 'Share'}
                  </button>
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-slate-900 font-semibold">{formatDateTimeObject(selectedPost.createdAt).date}</p>
                    <p className="text-[11px] text-slate-500">{formatDateTimeObject(selectedPost.createdAt).time}</p>
                  </div>
                  <button
                    onClick={() => setViewModal(false)}
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto p-4 sm:p-5 bg-gradient-to-b from-slate-50 to-white">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3.5">
                    <p className="text-xs uppercase text-blue-700 font-semibold mb-2">Traveler Contact</p>
                    <p className="text-slate-900 text-sm font-semibold">{selectedPost.userId?.first_name || '--'} {selectedPost.userId?.last_name || ''}</p>
                    <p className="text-slate-700 text-sm mt-1">{selectedPost.userId?.email || '--'}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-slate-700 text-sm">{selectedPost.userId?.phone || '--'}</span>
                      {selectedPost.userId?.phone ? (
                        <a
                          href={`tel:${selectedPost.userId.phone}`}
                          className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors flex-shrink-0"
                          title={`Call ${selectedPost.userId.phone}`}
                        >
                          <i className="bi bi-telephone-fill" style={{fontSize:'9px'}}></i>
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-3.5">
                    <p className="text-xs uppercase text-orange-700 font-semibold mb-2">Trip Route</p>
                    <p className="text-slate-800 text-sm"><span className="font-semibold">Date:</span> {formatDate(selectedPost.travelDate)}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">From:</span> {getLocationName(selectedPost.leavingFrom)}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">To:</span> {getLocationName(selectedPost.goingTo)}</p>
                  </div>

                  <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Other Details</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-[11px] text-slate-600 font-semibold">Compact</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2">
                      {[
                        { label: 'Adults / Children', value: `${selectedPost.adult ?? '--'} / ${selectedPost.child ?? '--'}` },
                        { label: 'Duration', value: selectedPost.duration || '--' },
                        { label: 'Total Budget', value: (selectedPost.budget != null && selectedPost.adult != null) ? `${(selectedPost.budget * selectedPost.adult)} ( ${selectedPost.budget || 0} * ${selectedPost.adult || 0} )` : '--' },
                        { label: 'Tour Type', value: selectedPost.tourType || '--' },
                        { label: 'Preferred Call', value: selectedPost.preferredCallTime || '--' },
                        { label: 'Will Book', value: selectedPost.willBook || '--' },
                        { label: 'Hotel Category', value: selectedPost.hotelCategory?.length ? selectedPost.hotelCategory.join(', ') : '--' },
                        { label: 'Contacted Agent', value: selectedPost.contactedAgent || '--' },
                        { label: 'Dates Flexible', value: selectedPost.datesFlexible || '--' },
                        { label: 'Most Important', value: selectedPost.mostImportant || '--' },
                        { label: 'Ticket / Reward', value: `${formatBool(selectedPost.haveTicket)} / ${formatBool(selectedPost.appliedForReward)}` }
                      ].map((item) => (
                        <div key={item.label} className="flex items-center text-[11px] leading-4 text-slate-800 min-h-[22px]">
                          <span className="min-w-[110px] text-slate-600 font-semibold">{item.label}:</span>
                          <span className="text-slate-900 truncate">{item.value}</span>
                        </div>
                      ))}
                    </div>
                    {selectedPost.description ? (
                      <div className="mt-3 rounded-lg bg-slate-50 p-2">
                        <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Description</p>
                        <p className="text-xs leading-5 text-slate-700 min-h-[26px] whitespace-pre-wrap">{selectedPost.description}</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {selectedPost.closeReason && (
                  <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50/70 p-3.5">
                    <p className="text-xs uppercase text-rose-700 font-semibold mb-2">Close Reason</p>
                    <p className="text-sm leading-6 text-slate-700 whitespace-pre-wrap">
                      {selectedPost.closeReason}
                    </p>
                  </div>
                )}

                <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 p-3.5">
                  <div className="flex flex-wrap items-center gap-2 mb-2.5">
                    <p className="text-xs uppercase text-violet-700 font-semibold mb-0">Lead Priority</p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] ${getAiResultBadge(selectedPost.airesult).color}`}>
                      {getAiResultBadge(selectedPost.airesult).label}
                    </span>
                    <span className="text-xs text-slate-600">Score: {formatAiScore(selectedPost.airesult)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Urgency:</span> {selectedPost.airesult?.urgency || '--'}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Confidence:</span> {selectedPost.airesult?.confidence ?? '--'}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Hot Lead:</span> {formatBool(selectedPost.airesult?.isHotLead)}</p>
                    <p className="text-sm text-slate-700 sm:col-span-2"><span className="font-semibold text-slate-900">Reason:</span> {selectedPost.airesult?.reason || '--'}</p>
                  </div>
                </div>

                {user?.role !== 'staff' && (
                  <div className="mt-4">
                    <p className="text-xs uppercase text-emerald-700 font-semibold mb-2">Buy By</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedPost.buyBy?.length > 0 ? (
                        selectedPost.buyBy.map((vendor) => (
                          <span key={vendor._id} className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs border border-emerald-100 font-medium">
                            {vendor.business_name || 'Business Name N/A'} - {(vendor.first_name || '')} {(vendor.last_name || '')}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 px-2.5 py-1 text-xs border border-red-100 font-medium">
                          No buyers yet
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex justify-end">
              <button
                onClick={() => setViewModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {sendLeadModal && sendLeadPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <i className="bi bi-person-plus text-purple-700"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Send to CRM</h3>
                <p className="text-xs text-gray-500">This will create a new lead in your CRM</p>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-5 text-xs text-slate-700 space-y-1">
              <div><span className="font-semibold text-slate-500">Name:</span> {sendLeadPost.userId?.first_name} {sendLeadPost.userId?.last_name}</div>
              <div><span className="font-semibold text-slate-500">Phone:</span> {sendLeadPost.userId?.phone || '--'}</div>
              <div><span className="font-semibold text-slate-500">From:</span> {getLocationName(sendLeadPost.leavingFrom)}</div>
              <div><span className="font-semibold text-slate-500">To:</span> {getLocationName(sendLeadPost.goingTo)}</div>
              <div><span className="font-semibold text-slate-500">Travel Date:</span> {formatDate(sendLeadPost.travelDate)}</div>
              <div><span className="font-semibold text-slate-500">Adults:</span> {sendLeadPost.adult || 0}</div>
              <div><span className="font-semibold text-slate-500">Hotel Category:</span> {sendLeadPost.hotelCategory?.length ? sendLeadPost.hotelCategory.join(', ') : 'Not Specified'}</div>
              <div><span className="font-semibold text-slate-500">Budget per person:</span> {sendLeadPost.budget ? `₹${sendLeadPost.budget}` : '--'}</div>
              {sendLeadPost.budget && sendLeadPost.adult && (
                <div className="pt-2 border-t border-slate-300">
                  <span className="font-semibold text-slate-600">Total Budget (in CRM):</span>
                  <span className="text-green-600 font-semibold ml-2">₹{(sendLeadPost.budget * sendLeadPost.adult).toLocaleString('en-IN')}</span>
                  <p className="text-[10px] text-slate-500 mt-1">(₹{sendLeadPost.budget} × {sendLeadPost.adult} adults)</p>
                </div>
              )}
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-600 mb-2">Send Lead To</label>
              <select
                value={selectedCrmMember}
                onChange={(e) => setSelectedCrmMember(e.target.value)}
                disabled={crmMembersLoading || !!sendingLeadId || crmMembers.length === 0}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
              >
                {crmMembersLoading ? (
                  <option value="">Loading CRM members...</option>
                ) : crmMembers.length > 0 ? (
                  crmMembers.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name}
                    </option>
                  ))
                ) : (
                  <option value="">No CRM members found</option>
                )}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeSendLeadModal}
                disabled={!!sendingLeadId}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={sendToMyLeads}
                disabled={!!sendingLeadId || crmMembersLoading || !selectedCrmMember}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center gap-2"
              >
                {sendingLeadId ? (
                  <><span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span> Adding...</>
                ) : (
                  <><i className="bi bi-person-plus"></i> Add to CRM</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {dmcModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="border-b border-slate-200 bg-gradient-to-r from-orange-50 to-blue-50 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-orange-500 flex items-center justify-center">
                  <i className="bi bi-building text-white text-sm"></i>
                </div>
                <h3 className="text-base font-bold text-slate-900">DMC & Itinerary Matches</h3>
              </div>
              <button
                onClick={() => { setDmcModal(false); setDmcData(null); }}
                className="h-7 w-7 rounded-lg hover:bg-white/50 text-slate-600 hover:text-slate-900 flex items-center justify-center transition-colors"
              >
                <i className="bi bi-x-lg text-sm"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {dmcLoadingId ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
              ) : dmcData ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Itineraries Column */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="bi bi-map text-green-600 text-lg"></i>
                      <h4 className="text-sm font-bold text-green-900">Itineraries</h4>
                      <span className="ml-auto text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        {dmcData.itineraries?.length || 0}
                      </span>
                    </div>
                    {dmcData.itineraries && dmcData.itineraries.length > 0 ? (
                      <div className="space-y-2">
                        {dmcData.itineraries.map((itinerary, idx) => (
                          <div key={idx} className="rounded-lg border border-green-200 bg-green-50/50 p-3 hover:shadow-sm transition-shadow">
                            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                              <div>
                                <span className="text-green-700 font-semibold">Duration:</span>
                                <span className="text-green-900 ml-1">{itinerary.duration || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-green-700 font-semibold">Pax:</span>
                                <span className="text-green-900 ml-1">{itinerary.adult || 0}A / {itinerary.child || 0}C</span>
                              </div>
                            </div>
                            {itinerary.dmc && (
                              <div className="pt-2 border-t border-green-200 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs">
                                  <i className="bi bi-building text-green-600"></i>
                                  <span className="font-semibold text-green-900">{itinerary.dmc.name}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-green-800">
                                  <i className="bi bi-telephone text-green-600"></i>
                                  <span>{itinerary.dmc.phone}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-green-800">
                                  <i className="bi bi-envelope text-green-600"></i>
                                  <span className="truncate">{itinerary.dmc.email}</span>
                                </div>
                              </div>
                            )}
                            {itinerary.pdfLink && (
                              <div className="mt-2 pt-2 border-t border-green-200">
                                <a
                                  href={itinerary.pdfLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                                >
                                  <i className="bi bi-file-pdf"></i>
                                  View PDF
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <i className="bi bi-inbox text-3xl mb-2 block"></i>
                        <p className="text-xs">No itineraries found</p>
                      </div>
                    )}
                  </div>

                  {/* DMCs Column */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="bi bi-building text-blue-600 text-lg"></i>
                      <h4 className="text-sm font-bold text-blue-900">DMCs</h4>
                      <span className="ml-auto text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                        {dmcData.dmcs?.length || 0}
                      </span>
                    </div>
                    {dmcData.dmcs && dmcData.dmcs.length > 0 ? (
                      <div className="space-y-2">
                        {dmcData.dmcs.map((dmc, idx) => (
                          <div key={idx} className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 hover:shadow-sm transition-shadow">
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-1.5 text-xs">
                                <i className="bi bi-building text-blue-600"></i>
                                <span className="font-semibold text-blue-900">{dmc.name}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-blue-800">
                                <i className="bi bi-telephone text-blue-600"></i>
                                <span>{dmc.phone}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-blue-800">
                                <i className="bi bi-envelope text-blue-600"></i>
                                <span className="truncate">{dmc.email}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <i className="bi bi-inbox text-3xl mb-2 block"></i>
                        <p className="text-xs">No DMCs found</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <i className="bi bi-search text-4xl mb-2 block"></i>
                  <p className="text-sm">No matching data found</p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex justify-end">
              <button
                onClick={() => { setDmcModal(false); setDmcData(null); }}
                className="px-4 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelPost;
